import { useState, useRef, useEffect } from "react";
import { uid, T, mono } from "./utils";
import { supabase } from "./supabase";

const DAYS_TR = ["Pzt","Sal","Çar","Per","Cum","Cmt","Paz"];
const SCENE_COLORS = ["#e4572e","#4a6fa5","#3a8a7c","#c9843a","#9b7ec8","#2d7a2d","#c0392b","#185FA5"];

// Sabit referans — çekim takviminde gerçek tarihlerin önemi yok, sadece
// haftaların 1. günü hep Pazartesi olacak şekilde kayması engellenmiş oluyor.
const FIXED_ANCHOR_MONDAY = new Date(2026, 5, 29); // 29 Haziran 2026 — Pazartesi

function getWeekStart(offset = 0) {
  const mon = new Date(FIXED_ANCHOR_MONDAY);
  mon.setDate(mon.getDate() + offset * 7);
  mon.setHours(0, 0, 0, 0);
  return mon;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function toDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,"0")}-${String(date.getDate()).padStart(2,"0")}`;
}

function formatDate(d) {
  return `${d.getDate()} ${["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"][d.getMonth()]}`;
}

// Supabase persist helper
async function saveSection(filmId, section, data, updatedBy) {
  await supabase.from("shooting_data").upsert(
    { film_id: filmId, section, data, updated_at: new Date().toISOString(), updated_by: updatedBy },
    { onConflict: "film_id,section" }
  );
}

async function loadSection(filmId, section) {
  const { data } = await supabase.from("shooting_data").select("data,updated_at,updated_by").eq("film_id", filmId).eq("section", section).single();
  return data;
}

const INITIAL_SCENES = [
  { id: uid(), number: "1", title: "Açılış sahnesi", location: "", notes: "", color: "#e4572e", interior: "İÇ", timeOfDay: "GÜNDÜZ" },
  { id: uid(), number: "2", title: "İç mekan — odada", location: "", notes: "", color: "#4a6fa5", interior: "İÇ", timeOfDay: "GÜNDÜZ" },
  { id: uid(), number: "3", title: "Dış mekan — sokak", location: "", notes: "", color: "#3a8a7c", interior: "DIŞ", timeOfDay: "GÜNDÜZ" },
];

function ScheduleView({ filmId = "atth", updatedBy = "" }) {
  const [scenes, setScenes] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [weekOffset, setWeekOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const [editingScene, setEditingScene] = useState(null);
  const [showSceneForm, setShowSceneForm] = useState(false);
  const [newScene, setNewScene] = useState({ number: "", title: "", location: "", notes: "", color: SCENE_COLORS[0], interior: "İÇ", timeOfDay: "GÜNDÜZ" });
  const [dragging, setDragging] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  useEffect(() => {
    loadSection(filmId, "schedule").then(row => {
      if (row?.data) {
        setScenes(row.data.scenes || INITIAL_SCENES);
        setSchedule(row.data.schedule || {});
        setWeekOffset(row.data.weekOffset || 0);
        setLastSaved({ at: row.updated_at, by: row.updated_by });
      } else {
        // Try localStorage migration for atth
        if (filmId === "atth") {
          try {
            const ls = localStorage.getItem("shooting_schedule") || localStorage.getItem("shooting_schedule_atth");
            if (ls) { const p = JSON.parse(ls); setScenes(p.scenes || INITIAL_SCENES); setSchedule(p.schedule || {}); setWeekOffset(p.weekOffset || 0); }
            else setScenes(INITIAL_SCENES);
          } catch { setScenes(INITIAL_SCENES); }
        } else { setScenes([]); }
      }
      setLoading(false);
    });
  }, [filmId]); // eslint-disable-line

  // Build 4-week grid
  const weekStart = getWeekStart(weekOffset);
  const days = Array.from({ length: 28 }, (_, i) => {
    const d = addDays(weekStart, i);
    return { date: d, dateStr: toDateStr(d), weekIdx: Math.floor(i / 7), dayNum: i + 1 };
  });
  const weeks = [0, 1, 2, 3];

  async function persist(newScenes, newSchedule, newOffset) {
    const data = { scenes: newScenes ?? scenes, schedule: newSchedule ?? schedule, weekOffset: newOffset ?? weekOffset };
    await saveSection(filmId, "schedule", data, updatedBy);
    setLastSaved({ at: new Date().toISOString(), by: updatedBy });
  }

  function addScene() {
    if (!newScene.title.trim()) return;
    const s = { ...newScene, id: uid() };
    const next = [...scenes, s];
    setScenes(next);
    persist(next, null, null);
    setNewScene({ number: "", title: "", location: "", notes: "", color: SCENE_COLORS[scenes.length % SCENE_COLORS.length] });
    setShowSceneForm(false);
  }

  function updateScene(id, patch) {
    const next = scenes.map(s => s.id === id ? { ...s, ...patch } : s);
    setScenes(next);
    persist(next, null, null);
  }

  function deleteScene(id) {
    const nextScenes = scenes.filter(s => s.id !== id);
    const nextSched = {};
    Object.entries(schedule).forEach(([date, ids]) => {
      const filtered = ids.filter(sid => sid !== id);
      if (filtered.length > 0) nextSched[date] = filtered;
    });
    setScenes(nextScenes);
    setSchedule(nextSched);
    persist(nextScenes, nextSched, null);
  }

  function assignScene(sceneId, dateStr) {
    const next = { ...schedule };
    // Remove from all dates first
    Object.keys(next).forEach(d => { next[d] = next[d].filter(id => id !== sceneId); });
    // Add to new date
    if (!next[dateStr]) next[dateStr] = [];
    next[dateStr] = [...next[dateStr], sceneId];
    setSchedule(next);
    persist(null, next, null);
  }

  function removeFromDate(sceneId, dateStr) {
    const next = { ...schedule };
    if (next[dateStr]) next[dateStr] = next[dateStr].filter(id => id !== sceneId);
    setSchedule(next);
    persist(null, next, null);
  }

  function moveScene(sceneId, fromDate, toDate, toIdx) {
    const next = { ...schedule };
    // Remove from source
    if (fromDate && next[fromDate]) {
      next[fromDate] = next[fromDate].filter(id => id !== sceneId);
    }
    // Add to target at position
    if (!next[toDate]) next[toDate] = [];
    else next[toDate] = next[toDate].filter(id => id !== sceneId);
    if (toIdx !== null && toIdx !== undefined) {
      next[toDate].splice(toIdx, 0, sceneId);
    } else {
      next[toDate] = [...next[toDate], sceneId];
    }
    setSchedule(next);
    persist(null, next, null);
  }

  // Drag from sidebar (unscheduled)
  function onDragStartSidebar(sceneId) {
    setDragging({ sceneId, fromDate: null });
  }

  // Drag from day cell
  function onDragStartCell(sceneId, fromDate) {
    setDragging({ sceneId, fromDate });
  }

  function onDragOverCell(e, dateStr, idx) {
    e.preventDefault();
    setDragOverDate(dateStr);
    setDragOverIdx(idx);
  }

  function onDropCell(e, dateStr) {
    e.preventDefault();
    if (!dragging) return;
    moveScene(dragging.sceneId, dragging.fromDate, dateStr, dragOverIdx);
    setDragging(null);
    setDragOverDate(null);
    setDragOverIdx(null);
  }

  function onDragEnd() {
    setDragging(null);
    setDragOverDate(null);
    setDragOverIdx(null);
  }

  const scene = id => scenes.find(s => s.id === id);
  const unscheduled = scenes.filter(s => !Object.values(schedule).flat().includes(s.id));
  const totalScheduled = Object.values(schedule).flat().length;

  if (loading) return <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ ...mono, color: T.mute }}>Yükleniyor…</span></div>;

  return (
    <div style={{ display: "flex", flex: 1, overflow: "hidden", background: T.bg }}>

      {/* Left sidebar — scene list */}
      <div style={{ width: 240, borderRight: `1px solid ${T.border}`, display: "flex", flexDirection: "column", flexShrink: 0, background: "#fff" }}>
        {/* Header */}
        <div style={{ padding: "20px 16px 12px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ ...mono, fontSize: 9, color: T.mute, marginBottom: 4 }}>ÇEKİM TAKVİMİ</div>
          <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-0.01em", marginBottom: 8 }}>Sahneler</div>
          <div style={{ display: "flex", gap: 6, flexDirection: "column" }}>
            <div style={{ ...mono, fontSize: 9, color: T.mute }}>{scenes.length} sahne · {totalScheduled} planlandı</div>
            {lastSaved && <div style={{ ...mono, fontSize: 8, color: T.mute }}>Son: {lastSaved.by || "?"} · {new Date(lastSaved.at).toLocaleDateString("tr-TR")}</div>}
          </div>
        </div>

        {/* Unscheduled label */}
        {unscheduled.length > 0 && (
          <div style={{ padding: "8px 16px 4px", ...mono, fontSize: 8, color: T.mute, letterSpacing: "0.1em" }}>PLANLANMAMIŞ</div>
        )}

        {/* Unscheduled scenes — draggable */}
        <div style={{ flex: 1, overflowY: "auto", padding: "4px 10px 10px" }}>
          {unscheduled.map(s => (
            <div key={s.id}
              draggable
              onDragStart={() => onDragStartSidebar(s.id)}
              onDragEnd={onDragEnd}
              onClick={() => setEditingScene(editingScene === s.id ? null : s.id)}
              style={{ background: T.bg, border: `1px solid ${T.border}`, borderLeft: `3px solid ${s.color}`, borderRadius: 3, padding: "8px 10px", marginBottom: 6, cursor: "grab", userSelect: "none" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom: 2 }}>
                    <span style={{ ...mono, fontSize: 9, color: s.color, fontWeight: 600 }}>SAHNE {s.number}</span>
                    <span style={{ ...mono, fontSize: 7, color:"#fff", background: s.interior==="DIŞ"?"#3a8a7c":"#4a6fa5", borderRadius:2, padding:"1px 4px" }}>{s.interior||"İÇ"}</span>
                    <span style={{ ...mono, fontSize: 7, color:"#fff", background: s.timeOfDay==="GECE"?"#111":"#c9843a", borderRadius:2, padding:"1px 4px" }}>{s.timeOfDay||"GÜNDÜZ"}</span>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: T.ink }}>{s.title}</div>
                  {s.location && <div style={{ fontSize: 10, color: T.mute, marginTop: 1 }}>{s.location}</div>}
                </div>
                <button onClick={e => { e.stopPropagation(); deleteScene(s.id); }}
                  style={{ background: "none", border: "none", color: T.mute, fontSize: 13, cursor: "pointer", padding: 0, opacity: 0.4 }}
                  onMouseEnter={e => e.currentTarget.style.opacity = "1"} onMouseLeave={e => e.currentTarget.style.opacity = "0.4"}>×</button>
              </div>
              {editingScene === s.id && (
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4 }} onClick={e => e.stopPropagation()}>
                  <input value={s.number} onChange={e => updateScene(s.id, { number: e.target.value })} placeholder="No"
                    style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 2, padding: "3px 6px", fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none", width: "100%" }} />
                  <input value={s.title} onChange={e => updateScene(s.id, { title: e.target.value })} placeholder="Sahne adı"
                    style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 2, padding: "3px 6px", fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none", width: "100%" }} />
                  <input value={s.location} onChange={e => updateScene(s.id, { location: e.target.value })} placeholder="Lokasyon"
                    style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 2, padding: "3px 6px", fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none", width: "100%" }} />
                  <div style={{ display:"flex", gap:4 }}>
                    <select value={s.interior||"İÇ"} onChange={e=>updateScene(s.id,{interior:e.target.value})} style={{flex:1,border:`1px solid ${T.border}`,borderRadius:2,padding:"3px 5px",fontSize:10,fontFamily:"'JetBrains Mono',monospace",outline:"none"}}>
                      <option>İÇ</option><option>DIŞ</option>
                    </select>
                    <select value={s.timeOfDay||"GÜNDÜZ"} onChange={e=>updateScene(s.id,{timeOfDay:e.target.value})} style={{flex:1,border:`1px solid ${T.border}`,borderRadius:2,padding:"3px 5px",fontSize:10,fontFamily:"'JetBrains Mono',monospace",outline:"none"}}>
                      <option>GÜNDÜZ</option><option>GECE</option>
                    </select>
                  </div>
                  <textarea value={s.notes} onChange={e => updateScene(s.id, { notes: e.target.value })} placeholder="Notlar"
                    rows={2} style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 2, padding: "3px 6px", fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none", width: "100%", resize: "none" }} />
                  <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    {SCENE_COLORS.map(c => (
                      <span key={c} onClick={() => updateScene(s.id, { color: c })}
                        style={{ width: 14, height: 14, borderRadius: "50%", background: c, cursor: "pointer", border: s.color === c ? "2px solid #111" : "1px solid transparent" }} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Add scene */}
          {showSceneForm ? (
            <div style={{ background: T.bg, border: `1px solid ${T.accent}`, borderRadius: 3, padding: "10px", marginBottom: 6 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <div style={{ display: "flex", gap: 5 }}>
                  <input value={newScene.number} onChange={e => setNewScene(p => ({ ...p, number: e.target.value }))} placeholder="No"
                    style={{ width: 40, background: "#fff", border: `1px solid ${T.border}`, borderRadius: 2, padding: "4px 6px", fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none" }} />
                  <input value={newScene.title} onChange={e => setNewScene(p => ({ ...p, title: e.target.value }))} placeholder="Sahne adı" autoFocus
                    onKeyDown={e => { if (e.key === "Enter") addScene(); if (e.key === "Escape") setShowSceneForm(false); }}
                    style={{ flex: 1, background: "#fff", border: `1px solid ${T.border}`, borderRadius: 2, padding: "4px 6px", fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none" }} />
                </div>
                <input value={newScene.location} onChange={e => setNewScene(p => ({ ...p, location: e.target.value }))} placeholder="Lokasyon"
                  style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 2, padding: "4px 6px", fontSize: 11, fontFamily: "'Inter',sans-serif", outline: "none", width: "100%" }} />
                <div style={{ display:"flex", gap:4 }}>
                  <select value={newScene.interior} onChange={e=>setNewScene(p=>({...p,interior:e.target.value}))} style={{flex:1,border:`1px solid ${T.border}`,borderRadius:2,padding:"4px 5px",fontSize:10,fontFamily:"'JetBrains Mono',monospace",outline:"none"}}>
                    <option>İÇ</option><option>DIŞ</option>
                  </select>
                  <select value={newScene.timeOfDay} onChange={e=>setNewScene(p=>({...p,timeOfDay:e.target.value}))} style={{flex:1,border:`1px solid ${T.border}`,borderRadius:2,padding:"4px 5px",fontSize:10,fontFamily:"'JetBrains Mono',monospace",outline:"none"}}>
                    <option>GÜNDÜZ</option><option>GECE</option>
                  </select>
                </div>
                <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  {SCENE_COLORS.map(c => (
                    <span key={c} onClick={() => setNewScene(p => ({ ...p, color: c }))}
                      style={{ width: 14, height: 14, borderRadius: "50%", background: c, cursor: "pointer", border: newScene.color === c ? "2px solid #111" : "1px solid transparent" }} />
                  ))}
                </div>
                <div style={{ display: "flex", gap: 5 }}>
                  <button onClick={addScene} style={{ flex: 1, background: T.ink, color: "#fff", border: "none", borderRadius: 2, padding: "5px", fontSize: 11, cursor: "pointer", fontWeight: 500 }}>Ekle</button>
                  <button onClick={() => setShowSceneForm(false)} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 2, padding: "5px 8px", fontSize: 11, cursor: "pointer", color: T.mute }}>İptal</button>
                </div>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowSceneForm(true)}
              style={{ width: "100%", background: "none", border: `1px dashed ${T.border}`, borderRadius: 3, padding: "8px", fontSize: 11, cursor: "pointer", color: T.mute, ...mono }}>
              + Sahne Ekle
            </button>
          )}

          {/* Scheduled scenes list */}
          {scenes.filter(s => Object.values(schedule).flat().includes(s.id)).length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ ...mono, fontSize: 8, color: T.mute, letterSpacing: "0.1em", marginBottom: 6 }}>PLANLANMIŞ</div>
              {scenes.filter(s => Object.values(schedule).flat().includes(s.id)).map(s => (
                <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 6px", marginBottom: 3, borderRadius: 2 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: T.mute, flex: 1 }}>S{s.number} {s.title}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main — calendar grid */}
      <div style={{ flex: 1, overflowX: "auto", overflowY: "auto", padding: "20px 16px" }}>
        {/* Week nav */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <button onClick={() => { const n = weekOffset - 1; setWeekOffset(n); persist(null, null, n); }}
            style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 2, padding: "4px 10px", cursor: "pointer", fontSize: 13, color: T.mute }}>←</button>
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            Gün {weekOffset*28 + 1} — Gün {weekOffset*28 + 28}
          </span>
          <button onClick={() => { const n = weekOffset + 1; setWeekOffset(n); persist(null, null, n); }}
            style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 2, padding: "4px 10px", cursor: "pointer", fontSize: 13, color: T.mute }}>→</button>
          <button onClick={() => { setWeekOffset(0); persist(null, null, 0); }}
            style={{ ...mono, fontSize: 9, background: "none", border: `1px solid ${T.border}`, borderRadius: 2, padding: "4px 8px", cursor: "pointer", color: T.mute, textTransform: "uppercase" }}>Başa Dön</button>
          <button onClick={() => {
            if (window.confirm("Takvimi sıfırlamak istediğine emin misin?")) {
              setSchedule({});
              persist(null, {}, null);
            }
          }} style={{ ...mono, fontSize: 9, background: "none", border: `1px solid #fcc`, borderRadius: 2, padding: "4px 8px", cursor: "pointer", color: "#c0392b", marginLeft: "auto", textTransform: "uppercase" }}>Sıfırla</button>
        </div>

        {/* 4 weeks */}
        {weeks.map(w => (
          <div key={w} style={{ marginBottom: 12 }}>
            <div style={{ ...mono, fontSize: 8, color: T.mute, marginBottom: 6, letterSpacing: "0.1em" }}>
              {w + 1}. HAFTA · GÜN {weekOffset*28 + w*7 + 1}–{weekOffset*28 + w*7 + 7}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
              {days.filter(d => d.weekIdx === w).map(({ date, dateStr, dayNum }) => {
                const isOver = dragOverDate === dateStr;
                const dayScenes = (schedule[dateStr] || []).map(id => scene(id)).filter(Boolean);

                return (
                  <div key={dateStr}
                    onDragOver={e => onDragOverCell(e, dateStr, dayScenes.length)}
                    onDrop={e => onDropCell(e, dateStr)}
                    style={{ minHeight: 90, background: isOver ? "#f0ece6" : "#fff", border: `1px solid ${isOver ? T.accent : T.border}`, borderRadius: 3, padding: "6px", transition: "all .1s" }}>
                    {/* Day header */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 5 }}>
                      <span style={{ ...mono, fontSize: 8, color: T.mute }}>{DAYS_TR[(date.getDay() + 6) % 7]}</span>
                      <span style={{ ...mono, fontSize: 9, fontWeight: 400, color: T.mute }}>{weekOffset*28 + dayNum}</span>
                    </div>

                    {/* Scenes in this day */}
                    {dayScenes.map((s, idx) => (
                      <div key={s.id}
                        draggable
                        onDragStart={() => onDragStartCell(s.id, dateStr)}
                        onDragEnd={onDragEnd}
                        onDragOver={e => { e.preventDefault(); setDragOverIdx(idx); }}
                        style={{ background: s.color + "18", border: `1px solid ${s.color}`, borderRadius: 2, padding: "3px 5px", marginBottom: 3, cursor: "grab", position: "relative" }}
                        onMouseEnter={e => e.currentTarget.querySelector(".rm-btn").style.opacity = "1"}
                        onMouseLeave={e => e.currentTarget.querySelector(".rm-btn").style.opacity = "0"}>
                        <div style={{ display:"flex", gap:3, alignItems:"center", marginBottom:1 }}>
                          <span style={{ ...mono, fontSize: 7, color: s.color, fontWeight: 600 }}>S{s.number}</span>
                          <span style={{ ...mono, fontSize: 6, color:"#fff", background: s.interior==="DIŞ"?"#3a8a7c":"#4a6fa5", borderRadius:1, padding:"0px 2px" }}>{s.interior||"İÇ"}</span>
                          <span style={{ ...mono, fontSize: 6, color:"#fff", background: s.timeOfDay==="GECE"?"#333":"#c9843a", borderRadius:1, padding:"0px 2px" }}>{s.timeOfDay||"G"}</span>
                        </div>
                        <div style={{ fontSize: 10, color: T.ink, lineHeight: 1.2, fontWeight: 500 }}>{s.title}</div>
                        {s.location && <div style={{ fontSize: 9, color: T.mute }}>{s.location}</div>}
                        <button className="rm-btn" onClick={() => removeFromDate(s.id, dateStr)}
                          style={{ position: "absolute", top: 2, right: 2, background: "none", border: "none", color: "#c0392b", fontSize: 11, cursor: "pointer", padding: 0, opacity: 0, lineHeight: 1 }}>×</button>
                      </div>
                    ))}

                    {/* Drop hint */}
                    {isOver && dayScenes.length === 0 && (
                      <div style={{ border: `2px dashed ${T.accent}`, borderRadius: 2, height: 30, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span style={{ ...mono, fontSize: 8, color: T.accent }}>bırak</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Script Timeline ────────────────────────────────────────────────────────
const SEQ_COLORS = ["#e4572e","#4a6fa5","#3a8a7c","#c9843a","#9b7ec8","#2d7a2d","#c0392b","#185FA5","#7c3a8a","#5a6a2d"];

function loadTimeline() {
  try { const s = localStorage.getItem("script_timeline"); return s ? JSON.parse(s) : null; } catch { return null; }
}
function saveTimeline(data) { localStorage.setItem("script_timeline", JSON.stringify(data)); }

const INITIAL_SEQUENCES = [
  { id: uid(), name: "Sekans 1", location: "İç — Oda", description: "Açılış sahnesi", color: "#e4572e", x: 40, y: 60, w: 180, h: 110 },
  { id: uid(), name: "Sekans 2", location: "Dış — Sokak", description: "Karakter yürüyor", color: "#4a6fa5", x: 260, y: 60, w: 180, h: 110 },
  { id: uid(), name: "Sekans 3", location: "İç — Mutfak", description: "Diyalog sahnesi", color: "#3a8a7c", x: 480, y: 60, w: 180, h: 110 },
];

function ScriptTimeline({ filmId = "atth", updatedBy = "" }) {
  const [sequences, setSequences] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newSeq, setNewSeq] = useState({ name: "", location: "", description: "", color: SEQ_COLORS[0] });
  const canvasRef = useRef();

  useEffect(() => {
    loadSection(filmId, "timeline").then(row => {
      if (row?.data) {
        setSequences(row.data.sequences || INITIAL_SEQUENCES);
        setConnections(row.data.connections || []);
        setLastSaved({ at: row.updated_at, by: row.updated_by });
      } else { setSequences(INITIAL_SEQUENCES); }
      setLoading(false);
    });
  }, [filmId]); // eslint-disable-line

  async function persist(seqs, conns) {
    const data = { sequences: seqs ?? sequences, connections: conns ?? connections };
    await saveSection(filmId, "timeline", data, updatedBy);
    setLastSaved({ at: new Date().toISOString(), by: updatedBy });
  }

  function onMouseDown(e, id) {
    if (connecting) {
      if (connecting !== id) {
        const exists = connections.find(c => c.from === connecting && c.to === id);
        if (!exists) {
          const next = [...connections, { id: uid(), from: connecting, to: id }];
          setConnections(next); persist(null, next);
        }
      }
      setConnecting(null); return;
    }
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDragging({ id, offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top });
  }

  function onMouseMove(e) {
    if (!dragging) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - dragging.offsetX;
    const y = e.clientY - rect.top - dragging.offsetY;
    const next = sequences.map(s => s.id === dragging.id ? { ...s, x: Math.max(0, x), y: Math.max(0, y) } : s);
    setSequences(next);
  }

  function onMouseUp() {
    if (dragging) { persist(sequences, null); setDragging(null); }
  }

  function deleteSeq(id) {
    const nextSeqs = sequences.filter(s => s.id !== id);
    const nextConns = connections.filter(c => c.from !== id && c.to !== id);
    setSequences(nextSeqs); setConnections(nextConns); persist(nextSeqs, nextConns);
  }

  function deleteConn(id) {
    const next = connections.filter(c => c.id !== id);
    setConnections(next); persist(null, next);
  }

  function addSeq() {
    if (!newSeq.name.trim()) return;
    const s = { ...newSeq, id: uid(), x: 40 + sequences.length * 30, y: 40 + sequences.length * 20, w: 180, h: 110 };
    const next = [...sequences, s];
    setSequences(next); persist(next, null);
    setNewSeq({ name: "", location: "", description: "", color: SEQ_COLORS[sequences.length % SEQ_COLORS.length] });
    setAdding(false);
  }

  function updateSeq(id, patch) {
    const next = sequences.map(s => s.id === id ? { ...s, ...patch } : s);
    setSequences(next); persist(next, null);
  }

  function connLine(conn) {
    const from = sequences.find(s => s.id === conn.from);
    const to = sequences.find(s => s.id === conn.to);
    if (!from || !to) return null;
    const x1 = from.x + (from.w || 180);
    const y1 = from.y + (from.h || 110) / 2;
    const x2 = to.x;
    const y2 = to.y + (to.h || 110) / 2;
    return { x1, y1, x2, y2, mx: (x1+x2)/2, my: (y1+y2)/2 };
  }

  const canvasW = Math.max(900, ...sequences.map(s => s.x + (s.w||180) + 60));
  const canvasH = Math.max(500, ...sequences.map(s => s.y + (s.h||110) + 60));

  if (loading) return <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ ...mono, color: T.mute }}>Yükleniyor…</span></div>;

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "12px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", gap: 8, alignItems: "center", background: "#fff", flexShrink: 0 }}>
        <span style={{ ...mono, fontSize: 9, color: T.mute }}>{sequences.length} sekans · {connections.length} bağlantı</span>
        {lastSaved && <span style={{ ...mono, fontSize: 8, color: T.mute }}>· Son: {lastSaved.by || "?"} · {new Date(lastSaved.at).toLocaleDateString("tr-TR")}</span>}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button onClick={() => setConnecting(connecting ? null : "pick")}
            style={{ ...mono, fontSize: 9, background: connecting ? T.accent : "none", color: connecting ? "#fff" : T.mute, border: `1px solid ${connecting ? T.accent : T.border}`, borderRadius: 2, padding: "4px 10px", cursor: "pointer" }}>
            {connecting === "pick" ? "Kaynak seç…" : connecting ? "Hedef seç…" : "Bağlantı Ekle"}
          </button>
          {adding ? (
            <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
              <input value={newSeq.name} onChange={e => setNewSeq(p => ({ ...p, name: e.target.value }))} placeholder="Sekans adı" autoFocus
                onKeyDown={e => { if (e.key === "Enter") addSeq(); if (e.key === "Escape") setAdding(false); }}
                style={{ border: `1px solid ${T.accent}`, borderRadius: 2, padding: "4px 8px", fontSize: 12, fontFamily: "'Inter',sans-serif", outline: "none", width: 130 }} />
              <input value={newSeq.location} onChange={e => setNewSeq(p => ({ ...p, location: e.target.value }))} placeholder="Mekan"
                style={{ border: `1px solid ${T.border}`, borderRadius: 2, padding: "4px 8px", fontSize: 12, fontFamily: "'Inter',sans-serif", outline: "none", width: 90 }} />
              <div style={{ display: "flex", gap: 2 }}>
                {SEQ_COLORS.map(c => <span key={c} onClick={() => setNewSeq(p => ({ ...p, color: c }))}
                  style={{ width: 12, height: 12, borderRadius: "50%", background: c, cursor: "pointer", border: newSeq.color === c ? "2px solid #111" : "1px solid transparent" }} />)}
              </div>
              <button onClick={addSeq} style={{ background: T.ink, color: "#fff", border: "none", borderRadius: 2, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>Ekle</button>
              <button onClick={() => setAdding(false)} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 2, padding: "4px 8px", fontSize: 11, cursor: "pointer", color: T.mute }}>İptal</button>
            </div>
          ) : (
            <button onClick={() => setAdding(true)} style={{ background: T.ink, color: "#fff", border: "none", borderRadius: 2, padding: "4px 12px", fontSize: 11, cursor: "pointer", fontWeight: 500 }}>+ Sekans</button>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto", background: "#f8f6f2", position: "relative" }}>
        <div ref={canvasRef} onMouseMove={onMouseMove} onMouseUp={onMouseUp}
          onClick={() => { if (connecting) setConnecting(null); }}
          style={{ position: "relative", width: canvasW, height: canvasH, userSelect: "none" }}>

          <svg style={{ position: "absolute", top: 0, left: 0, width: canvasW, height: canvasH, pointerEvents: "none" }}>
            <defs>
              <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                <path d="M0,0 L0,6 L8,3 z" fill="#bbb" />
              </marker>
            </defs>
            {connections.map(conn => {
              const l = connLine(conn);
              if (!l) return null;
              return (
                <g key={conn.id}>
                  <path d={`M${l.x1},${l.y1} C${l.x1+40},${l.y1} ${l.x2-40},${l.y2} ${l.x2},${l.y2}`}
                    stroke="#ccc" strokeWidth={1.5} fill="none" markerEnd="url(#arrow)" />
                  <circle cx={l.mx} cy={l.my} r={6} fill="#fff" stroke="#ddd" strokeWidth={1}
                    style={{ cursor: "pointer", pointerEvents: "all" }}
                    onClick={() => deleteConn(conn.id)} />
                  <text x={l.mx} y={l.my+4} textAnchor="middle" fontSize={8} fill="#bbb" style={{ pointerEvents: "none" }}>×</text>
                </g>
              );
            })}
          </svg>

          {sequences.map(s => (
            <div key={s.id}
              onMouseDown={e => { if (connecting === "pick") { setConnecting(s.id); e.stopPropagation(); } else if (connecting) { onMouseDown(e, s.id); } else onMouseDown(e, s.id); }}
              style={{
                position: "absolute", left: s.x, top: s.y, width: s.w || 180, minHeight: s.h || 110,
                background: "#fff", border: `2px solid ${s.color}`, borderRadius: 6,
                boxShadow: dragging?.id === s.id ? "0 8px 24px rgba(0,0,0,0.15)" : "0 2px 8px rgba(0,0,0,0.06)",
                cursor: connecting ? "crosshair" : dragging?.id === s.id ? "grabbing" : "grab",
                zIndex: dragging?.id === s.id ? 100 : 1,
              }}>
              <div style={{ background: s.color, padding: "6px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", borderRadius: "4px 4px 0 0" }}>
                {editing === s.id ? (
                  <input value={s.name} onChange={e => updateSeq(s.id, { name: e.target.value })}
                    onBlur={() => setEditing(null)} autoFocus
                    style={{ flex: 1, background: "rgba(255,255,255,0.2)", border: "none", outline: "none", color: "#fff", fontSize: 11, fontWeight: 600, fontFamily: "'Inter',sans-serif" }} />
                ) : (
                  <span onDoubleClick={() => setEditing(s.id)} style={{ fontSize: 11, fontWeight: 600, color: "#fff", flex: 1 }}>{s.name}</span>
                )}
                <button onClick={e => { e.stopPropagation(); deleteSeq(s.id); }}
                  style={{ background: "none", border: "none", color: "rgba(255,255,255,0.7)", fontSize: 13, cursor: "pointer", padding: 0 }}>×</button>
              </div>
              <div style={{ padding: "8px 10px", display: "flex", flexDirection: "column", gap: 4 }} onClick={e => e.stopPropagation()}>
                <input value={s.location} onChange={e => updateSeq(s.id, { location: e.target.value })} placeholder="Mekan…"
                  style={{ width: "100%", border: "none", borderBottom: `1px solid ${T.borderLight}`, background: "none", fontSize: 10, fontFamily: "'JetBrains Mono',monospace", color: T.mute, outline: "none", padding: "2px 0" }} />
                <textarea value={s.description} onChange={e => updateSeq(s.id, { description: e.target.value })} placeholder="Sekans açıklaması…"
                  rows={3} style={{ width: "100%", border: "none", background: "none", fontSize: 11, fontFamily: "'Inter',sans-serif", color: T.ink, outline: "none", resize: "none", lineHeight: 1.4, padding: 0, marginTop: 2 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Casting ────────────────────────────────────────────────────────────────
function loadCasting() {
  try { const s = localStorage.getItem("casting_data"); return s ? JSON.parse(s) : null; } catch { return null; }
}
function saveCasting(data) { localStorage.setItem("casting_data", JSON.stringify(data)); }

function CastingView({ filmId = "atth", updatedBy = "" }) {
  const [chars, setChars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const [draggingCand, setDraggingCand] = useState(null);
  const [dragOverChar, setDragOverChar] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [newCharName, setNewCharName] = useState("");
  const [addingChar, setAddingChar] = useState(false);

  useEffect(() => {
    loadSection(filmId, "casting").then(row => {
      if (row?.data) {
        setChars(row.data.chars || []);
        setLastSaved({ at: row.updated_at, by: row.updated_by });
      } else { setChars([]); }
      setLoading(false);
    });
  }, [filmId]); // eslint-disable-line

  async function persist(next) {
    await saveSection(filmId, "casting", { chars: next }, updatedBy);
    setLastSaved({ at: new Date().toISOString(), by: updatedBy });
  }

  function addChar() {
    if (!newCharName.trim()) return;
    const next = [...chars, { id: uid(), name: newCharName.trim(), candidates: [] }];
    setChars(next); persist(next); setNewCharName(""); setAddingChar(false);
  }

  function deleteChar(id) {
    const next = chars.filter(c => c.id !== id);
    setChars(next); persist(next);
  }

  function updateCharName(id, name) {
    const next = chars.map(c => c.id === id ? { ...c, name } : c);
    setChars(next); persist(next);
  }

  function addCandidate(charId, name) {
    if (!name.trim()) return;
    const next = chars.map(c => c.id === charId ? { ...c, candidates: [...c.candidates, { id: uid(), name: name.trim() }] } : c);
    setChars(next); persist(next);
  }

  function removeCandidate(charId, candId) {
    const next = chars.map(c => c.id === charId ? { ...c, candidates: c.candidates.filter(x => x.id !== candId) } : c);
    setChars(next); persist(next);
  }

  function onDragStartCand(charId, candId) { setDraggingCand({ charId, candId }); }

  function onDragOverCand(e, charId, idx) { e.preventDefault(); setDragOverChar(charId); setDragOverIdx(idx); }

  function onDropCand(e, toCharId) {
    e.preventDefault();
    if (!draggingCand) return;
    const { charId: fromCharId, candId } = draggingCand;
    let next = chars.map(c => ({ ...c, candidates: [...c.candidates] }));
    const fromChar = next.find(c => c.id === fromCharId);
    const cand = fromChar?.candidates.find(x => x.id === candId);
    if (!cand) return;
    fromChar.candidates = fromChar.candidates.filter(x => x.id !== candId);
    const toChar = next.find(c => c.id === toCharId);
    const idx = dragOverIdx !== null ? dragOverIdx : toChar.candidates.length;
    toChar.candidates.splice(idx, 0, cand);
    setChars(next); persist(next);
    setDraggingCand(null); setDragOverChar(null); setDragOverIdx(null);
  }

  if (loading) return <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ ...mono, color: T.mute }}>Yükleniyor…</span></div>;

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 20, paddingBottom: 12, borderBottom: `1px solid ${T.border}` }}>
        <div>
          <div style={{ ...mono, fontSize: 9, color: T.mute, marginBottom: 4 }}>CASTING</div>
          <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: "-0.02em" }}>Karakter Listesi</div>
          {lastSaved && <div style={{ ...mono, fontSize: 8, color: T.mute, marginTop: 4 }}>Son: {lastSaved.by || "?"} · {new Date(lastSaved.at).toLocaleDateString("tr-TR")}</div>}
        </div>
        <div>
          {addingChar ? (
            <div style={{ display: "flex", gap: 6 }}>
              <input value={newCharName} onChange={e => setNewCharName(e.target.value)} placeholder="Karakter adı" autoFocus
                onKeyDown={e => { if (e.key === "Enter") addChar(); if (e.key === "Escape") setAddingChar(false); }}
                style={{ border: `1px solid ${T.accent}`, borderRadius: 2, padding: "5px 10px", fontSize: 12, fontFamily: "'Inter',sans-serif", outline: "none", width: 160 }} />
              <button onClick={addChar} style={{ background: T.ink, color: "#fff", border: "none", borderRadius: 2, padding: "5px 12px", fontSize: 11, cursor: "pointer" }}>Ekle</button>
              <button onClick={() => setAddingChar(false)} style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 2, padding: "5px 8px", fontSize: 11, cursor: "pointer", color: T.mute }}>İptal</button>
            </div>
          ) : (
            <button onClick={() => setAddingChar(true)} style={{ background: T.ink, color: "#fff", border: "none", borderRadius: 2, padding: "6px 14px", fontSize: 11, cursor: "pointer", fontWeight: 500 }}>+ Karakter Ekle</button>
          )}
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
        {chars.map(ch => (
          <CharCard key={ch.id} ch={ch}
            isDragOver={dragOverChar === ch.id}
            onDragOver={(e, idx) => onDragOverCand(e, ch.id, idx)}
            onDrop={e => onDropCand(e, ch.id)}
            onDragStartCand={candId => onDragStartCand(ch.id, candId)}
            onRemoveCand={candId => removeCandidate(ch.id, candId)}
            onAddCand={name => addCandidate(ch.id, name)}
            onDelete={() => deleteChar(ch.id)}
            onRename={name => updateCharName(ch.id, name)}
          />
        ))}
      </div>
    </div>
  );
}

function CharCard({ ch, isDragOver, onDragOver, onDrop, onDragStartCand, onRemoveCand, onAddCand, onDelete, onRename }) {
  const [newCand, setNewCand] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(ch.name);

  const commitName = () => { setEditingName(false); if (nameVal.trim() && nameVal !== ch.name) onRename(nameVal.trim()); };

  return (
    <div onDragOver={e => onDragOver(e, ch.candidates.length)} onDrop={onDrop}
      style={{ background: "#fff", border: `1px solid ${isDragOver ? T.accent : T.border}`, borderRadius: 6, width: 200, padding: "12px", display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {editingName ? (
          <input value={nameVal} onChange={e => setNameVal(e.target.value)} onBlur={commitName}
            onKeyDown={e => { if (e.key === "Enter") commitName(); if (e.key === "Escape") { setNameVal(ch.name); setEditingName(false); } }}
            autoFocus style={{ flex: 1, fontSize: 12, fontWeight: 600, border: `1px solid ${T.accent}`, borderRadius: 2, padding: "1px 6px", fontFamily: "'Inter',sans-serif", outline: "none" }} />
        ) : (
          <span onDoubleClick={() => setEditingName(true)} style={{ fontSize: 12, fontWeight: 600, color: T.ink, cursor: "text", flex: 1 }}>{ch.name}</span>
        )}
        <button onClick={onDelete} style={{ background: "none", border: "none", color: T.mute, fontSize: 13, cursor: "pointer", padding: 0, opacity: 0.4 }}
          onMouseEnter={e => e.currentTarget.style.opacity = "1"} onMouseLeave={e => e.currentTarget.style.opacity = "0.4"}>×</button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {ch.candidates.map((cand, idx) => (
          <div key={cand.id} draggable onDragStart={() => onDragStartCand(cand.id)}
            onDragOver={e => { e.preventDefault(); e.stopPropagation(); onDragOver(e, idx); }}
            style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 7px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 3, cursor: "grab" }}
            onMouseEnter={e => e.currentTarget.querySelector(".rm").style.opacity = "1"}
            onMouseLeave={e => e.currentTarget.querySelector(".rm").style.opacity = "0"}>
            <span style={{ ...mono, fontSize: 8, color: T.mute, flexShrink: 0, minWidth: 10 }}>{idx + 1}</span>
            <span style={{ fontSize: 11, color: T.ink, flex: 1 }}>{cand.name}</span>
            <button className="rm" onClick={() => onRemoveCand(cand.id)}
              style={{ background: "none", border: "none", color: "#c0392b", fontSize: 11, cursor: "pointer", padding: 0, opacity: 0, transition: "opacity .15s", lineHeight: 1 }}>×</button>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        <input value={newCand} onChange={e => setNewCand(e.target.value)} placeholder="Oyuncu adı…"
          onKeyDown={e => { if (e.key === "Enter") { onAddCand(newCand); setNewCand(""); } }}
          style={{ flex: 1, border: `1px solid ${T.border}`, borderRadius: 2, padding: "4px 6px", fontSize: 10, fontFamily: "'Inter',sans-serif", outline: "none", background: T.bg }} />
        <button onClick={() => { onAddCand(newCand); setNewCand(""); }}
          style={{ background: T.ink, color: "#fff", border: "none", borderRadius: 2, padding: "4px 7px", fontSize: 12, cursor: "pointer" }}>+</button>
      </div>
    </div>
  );
}

// ─── Location Scout ──────────────────────────────────────────────────────────
function LocationCard({ loc, scenes, onUpdate, onDelete, onUploadPhoto, onRemovePhoto, uploading }) {
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(loc.name);
  const [showSceneMenu, setShowSceneMenu] = useState(false);
  const fileRef = useRef();
  const menuRef = useRef();

  useEffect(() => {
    const h = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowSceneMenu(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const commitName = () => { setEditingName(false); if (nameVal.trim() && nameVal !== loc.name) onUpdate({ name: nameVal.trim() }); };
  const taggedNumbers = loc.sceneNumbers || [];
  const toggleScene = (num) => {
    const next = taggedNumbers.includes(num) ? taggedNumbers.filter(n => n !== num) : [...taggedNumbers, num];
    onUpdate({ sceneNumbers: next });
  };

  return (
    <div style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      {/* Photo gallery */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 2, height: 130, background: T.bg }}>
        {(loc.photos || []).slice(0, 3).map((p, i) => (
          <div key={p.id} style={{ position: "relative", overflow: "hidden" }}>
            <img src={p.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }}
              onClick={() => window.open(p.url, "_blank")} />
            {i === 2 && (loc.photos || []).length > 3 && (
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", ...mono, fontSize: 11 }}>
                +{(loc.photos || []).length - 3}
              </div>
            )}
            <button onClick={() => onRemovePhoto(p.id)}
              style={{ position: "absolute", top: 3, right: 3, width: 16, height: 16, borderRadius: "50%", background: "rgba(0,0,0,0.5)", color: "#fff", border: "none", fontSize: 10, cursor: "pointer", lineHeight: 1 }}>×</button>
          </div>
        ))}
        <div onClick={() => !uploading && fileRef.current.click()}
          style={{ display: (loc.photos || []).length === 0 ? "flex" : (loc.photos || []).length < 3 ? "flex" : "none", alignItems: "center", justifyContent: "center", cursor: uploading ? "wait" : "pointer", border: `1px dashed ${T.border}`, ...mono, fontSize: 9, color: T.mute }}>
          {uploading ? "…" : "+ foto"}
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
          onChange={e => { if (e.target.files[0]) onUploadPhoto(e.target.files[0]); e.target.value = ""; }} />
      </div>

      <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          {editingName ? (
            <input value={nameVal} onChange={e => setNameVal(e.target.value)} onBlur={commitName}
              onKeyDown={e => { if (e.key === "Enter") commitName(); if (e.key === "Escape") { setNameVal(loc.name); setEditingName(false); } }}
              autoFocus style={{ fontSize: 15, fontWeight: 600, border: `1px solid ${T.accent}`, borderRadius: 2, padding: "1px 6px", fontFamily: "'Inter',sans-serif", outline: "none", flex: 1 }} />
          ) : (
            <span onDoubleClick={() => setEditingName(true)} style={{ fontSize: 15, fontWeight: 600, color: T.ink, cursor: "text" }}>{loc.name || "İsimsiz mekan"}</span>
          )}
          <button onClick={onDelete} style={{ background: "none", border: "none", color: T.mute, fontSize: 14, cursor: "pointer", padding: 0, opacity: 0.4, flexShrink: 0 }}
            onMouseEnter={e => e.currentTarget.style.opacity = "1"} onMouseLeave={e => e.currentTarget.style.opacity = "0.4"}>×</button>
        </div>

        <input value={loc.address || ""} onChange={e => onUpdate({ address: e.target.value })} placeholder="Adres / konum"
          style={{ fontSize: 12, color: T.mute, border: "none", outline: "none", fontFamily: "'Inter',sans-serif", padding: 0, background: "transparent" }} />

        <textarea value={loc.description || ""} onChange={e => onUpdate({ description: e.target.value })} placeholder="Mekanı tarif et — ışık, atmosfer, erişim, izin durumu…"
          rows={2}
          style={{ fontSize: 13, color: "#444", border: `1px solid ${T.borderLight}`, borderRadius: 4, padding: "6px 8px", fontFamily: "'Inter',sans-serif", outline: "none", resize: "vertical", background: T.bg, lineHeight: 1.5 }} />

        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center", position: "relative" }} ref={menuRef}>
          {taggedNumbers.map(num => {
            const s = scenes.find(sc => sc.number === num);
            return (
              <span key={num} onClick={() => toggleScene(num)} title={s?.title || ""}
                style={{ ...mono, fontSize: 9, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 4, padding: "3px 7px", color: T.mute, cursor: "pointer" }}>
                S{num} ×
              </span>
            );
          })}
          <span onClick={() => setShowSceneMenu(s => !s)}
            style={{ ...mono, fontSize: 9, color: T.accent, cursor: "pointer", padding: "3px 4px" }}>+ sahne</span>
          {showSceneMenu && (
            <div style={{ position: "absolute", top: "120%", left: 0, background: "#fff", border: `1px solid ${T.border}`, borderRadius: 4, zIndex: 50, maxHeight: 200, overflowY: "auto", minWidth: 200, boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}>
              {scenes.length === 0 && <div style={{ padding: 10, ...mono, fontSize: 9, color: T.mute }}>Henüz sahne yok</div>}
              {scenes.map(s => (
                <div key={s.id} onClick={() => toggleScene(s.number)}
                  style={{ padding: "7px 10px", fontSize: 12, cursor: "pointer", display: "flex", gap: 8, background: taggedNumbers.includes(s.number) ? T.bg : "#fff" }}
                  onMouseEnter={e => e.currentTarget.style.background = T.bg}
                  onMouseLeave={e => e.currentTarget.style.background = taggedNumbers.includes(s.number) ? T.bg : "#fff"}>
                  <span style={{ ...mono, fontSize: 9, color: T.mute, flexShrink: 0 }}>S{s.number}</span>
                  <span style={{ color: T.ink }}>{s.title}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LocationScoutView({ filmId, updatedBy }) {
  const [locations, setLocations] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);

  useEffect(() => {
    Promise.all([
      loadSection(filmId, "locations"),
      loadSection(filmId, "schedule"),
    ]).then(([locRow, schedRow]) => {
      setLocations(locRow?.data?.locations || []);
      setScenes(schedRow?.data?.scenes || []);
      setLoading(false);
    });
  }, [filmId]); // eslint-disable-line

  async function persist(next) {
    setLocations(next);
    await saveSection(filmId, "locations", { locations: next }, updatedBy);
  }

  function addLocation() {
    const loc = { id: uid(), name: "", address: "", description: "", photos: [], sceneNumbers: [] };
    persist([...locations, loc]);
  }

  function updateLocation(id, patch) {
    persist(locations.map(l => l.id === id ? { ...l, ...patch } : l));
  }

  async function deleteLocation(id) {
    if (!window.confirm("Bu mekanı silmek istediğine emin misin?")) return;
    const loc = locations.find(l => l.id === id);
    const paths = (loc?.photos || []).map(p => p.path).filter(Boolean);
    if (paths.length) await supabase.storage.from("shooting-locations").remove(paths);
    persist(locations.filter(l => l.id !== id));
  }

  async function uploadPhoto(locId, file) {
    setUploadingId(locId);
    const path = `${filmId}/${locId}/${uid()}-${file.name.replace(/[^a-zA-Z0-9.\-_]/g, "_")}`;
    const { error } = await supabase.storage.from("shooting-locations").upload(path, file);
    if (error) {
      alert("Yükleme hatası: " + error.message);
      setUploadingId(null);
      return;
    }
    const { data } = supabase.storage.from("shooting-locations").getPublicUrl(path);
    const loc = locations.find(l => l.id === locId);
    const nextPhotos = [...(loc.photos || []), { id: uid(), path, url: data.publicUrl }];
    await persist(locations.map(l => l.id === locId ? { ...l, photos: nextPhotos } : l));
    setUploadingId(null);
  }

  async function removePhoto(locId, photoId) {
    const loc = locations.find(l => l.id === locId);
    const photo = (loc.photos || []).find(p => p.id === photoId);
    if (photo?.path) await supabase.storage.from("shooting-locations").remove([photo.path]);
    persist(locations.map(l => l.id === locId ? { ...l, photos: (l.photos || []).filter(p => p.id !== photoId) } : l));
  }

  if (loading) return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span style={{ ...mono, color: T.mute }}>Loading</span>
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 20 }}>
        <div style={{ ...mono, fontSize: 9, color: T.mute, letterSpacing: "0.1em" }}>{locations.length} MEKAN</div>
        <button onClick={addLocation} style={{ ...mono, fontSize: 10, background: T.ink, color: "#fff", border: "none", borderRadius: 4, padding: "7px 14px", cursor: "pointer" }}>+ Mekan Ekle</button>
      </div>

      {locations.length === 0 ? (
        <div style={{ ...mono, fontSize: 10, color: T.mute, padding: "40px 0", textAlign: "center" }}>
          Henüz mekan eklenmedi. "+ Mekan Ekle" ile başla.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 16 }}>
          {locations.map(loc => (
            <LocationCard key={loc.id} loc={loc} scenes={scenes}
              onUpdate={patch => updateLocation(loc.id, patch)}
              onDelete={() => deleteLocation(loc.id)}
              onUploadPhoto={file => uploadPhoto(loc.id, file)}
              onRemovePhoto={photoId => removePhoto(loc.id, photoId)}
              uploading={uploadingId === loc.id} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main ShootingView ───────────────────────────────────────────────────────
const SHOOTING_FILMS = [
  { id: "atth", label: "All the Tired Horses" },
  { id: "eller", label: "ELLER" },
];

export function ShootingView() {
  const [filmId, setFilmId] = useState(null);
  const [section, setSection] = useState(null);
  const [userName, setUserName] = useState(() => localStorage.getItem("shooting_user") || "");
  const [nameInput, setNameInput] = useState("");

  // Kullanıcı adı yoksa sor
  if (!userName) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: T.bg, gap: 16 }}>
        <div style={{ ...mono, fontSize: 9, color: T.mute, marginBottom: 8 }}>SHOOTING — GİRİŞ</div>
        <div style={{ fontSize: 14, color: T.ink, marginBottom: 4 }}>Adını yaz:</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input value={nameInput} onChange={e => setNameInput(e.target.value)} placeholder="Burak / Dilşad / …" autoFocus
            onKeyDown={e => { if (e.key === "Enter" && nameInput.trim()) { localStorage.setItem("shooting_user", nameInput.trim()); setUserName(nameInput.trim()); } }}
            style={{ border: `1px solid ${T.border}`, borderRadius: 3, padding: "8px 14px", fontSize: 14, fontFamily: "'Inter',sans-serif", outline: "none", width: 200 }} />
          <button onClick={() => { if (nameInput.trim()) { localStorage.setItem("shooting_user", nameInput.trim()); setUserName(nameInput.trim()); } }}
            style={{ background: T.ink, color: "#fff", border: "none", borderRadius: 3, padding: "8px 16px", fontSize: 13, cursor: "pointer" }}>Gir</button>
        </div>
      </div>
    );
  }

  if (!filmId) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: T.bg, gap: 16 }}>
        <div style={{ ...mono, fontSize: 9, color: T.mute, marginBottom: 8, letterSpacing: "0.2em" }}>SHOOTING</div>
        {SHOOTING_FILMS.map(f => (
          <button key={f.id} onClick={() => setFilmId(f.id)}
            style={{ width: 320, padding: "20px 26px", background: "#fff", border: `1px solid ${T.border}`, borderRadius: 6, cursor: "pointer", textAlign: "left", display: "flex", flexDirection: "column", gap: 5 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = "none"; }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: T.ink }}>{f.label}</span>
          </button>
        ))}
      </div>
    );
  }

  const filmLabel = SHOOTING_FILMS.find(f => f.id === filmId)?.label;

  if (!section) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: T.bg, gap: 16 }}>
        <button onClick={() => setFilmId(null)} style={{ ...mono, fontSize: 9, background: "none", border: "none", color: T.mute, cursor: "pointer", marginBottom: 4 }}>← Filmler</button>
        <div style={{ ...mono, fontSize: 11, color: T.ink, marginBottom: 4 }}>{filmLabel}</div>
        {[
          { id: "schedule", label: "Schedule", desc: "Çekim takvimi ve sahne planlaması" },
          { id: "timeline", label: "Script Timeline", desc: "Sekansları görsel olarak bağla ve düzenle" },
          { id: "casting", label: "Casting", desc: "Karakterler ve oyuncu adayları" },
          { id: "locations", label: "Location Scout", desc: "Mekanlar, referans fotoğraflar ve notlar" },
        ].map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            style={{ width: 320, padding: "20px 26px", background: "#fff", border: `1px solid ${T.border}`, borderRadius: 6, cursor: "pointer", textAlign: "left", display: "flex", flexDirection: "column", gap: 5 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.accent; e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = "none"; }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: T.ink }}>{s.label}</span>
            <span style={{ ...mono, fontSize: 9, color: T.mute }}>{s.desc}</span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "10px 16px", borderBottom: `1px solid ${T.border}`, background: "#fff", flexShrink: 0, display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={() => setSection(null)}
          style={{ ...mono, fontSize: 9, background: "none", border: "none", color: T.mute, cursor: "pointer", padding: 0 }}>← Geri</button>
        <span style={{ ...mono, fontSize: 9, color: T.mute }}>·</span>
        <span style={{ ...mono, fontSize: 9, color: T.mute }}>{filmLabel}</span>
        <span style={{ ...mono, fontSize: 9, color: T.mute }}>·</span>
        <span style={{ ...mono, fontSize: 9, color: T.ink, textTransform: "uppercase" }}>
          {section === "schedule" ? "Schedule" : section === "timeline" ? "Script Timeline" : section === "casting" ? "Casting" : "Location Scout"}
        </span>
      </div>
      {section === "schedule" && <ScheduleView filmId={filmId} updatedBy={userName} />}
      {section === "timeline" && <ScriptTimeline filmId={filmId} updatedBy={userName} />}
      {section === "casting" && <CastingView filmId={filmId} updatedBy={userName} />}
      {section === "locations" && <LocationScoutView filmId={filmId} updatedBy={userName} />}
    </div>
  );
}
